/**
 * AccountTree sub-components and helpers.
 *
 * Extracted from AccountTree.tsx to keep each function under 50 lines.
 */

import { Fragment } from 'react'

import { Skeleton } from '@elsapiens/ui'
import { cn } from '@elsapiens/utils'

import { ACCOUNT_TYPES } from './chart-of-accounts-helpers'
import { ACCOUNT_TYPE_LABELS } from '../../constants/labels'

import type { AccountType } from '../../services/account-service'

// =============================================================================
// Table column / header config
// =============================================================================

export const HEADER_LABELS = ['Code', 'Name', 'Balance', 'Subtype', 'Tags', 'Currency', 'Status', 'Actions'] as const
export type HeaderLabel = typeof HEADER_LABELS[number]

export function ColGroup(): JSX.Element {
  return (
    <colgroup>
      <col style={{ width: '11rem' }} />
      <col />
      <col style={{ width: '9rem' }} />
      <col style={{ width: '13rem' }} />
      <col style={{ width: '9rem' }} />
      <col style={{ width: '5.5rem' }} />
      <col style={{ width: '6rem' }} />
      <col style={{ width: '7.5rem' }} />
    </colgroup>
  )
}

interface HeaderRowProps {
  labels?: readonly string[]
  withAlignment?: boolean
}

export function HeaderRow({ labels = HEADER_LABELS, withAlignment = true }: HeaderRowProps): JSX.Element {
  return (
    <thead>
      <tr className="border-b border-border bg-muted">
        {labels.map((h, i) => (
          <th
            key={h}
            className={cn(
              'font-medium text-foreground text-left whitespace-nowrap px-3 py-2.5',
              withAlignment && (h === 'Balance' || h === 'Currency') && 'text-right',
              i === 0 && 'pl-4',
              i === 7 && 'pr-4',
            )}
          >
            {h}
          </th>
        ))}
      </tr>
    </thead>
  )
}

// =============================================================================
// Group header row
// =============================================================================

export interface GroupRowData {
  id: string
  accountType: AccountType
  isExpanded: boolean
  count: number
}

interface GroupHeaderRowProps {
  row: GroupRowData
  cols: number
  isFirst: boolean
  onToggle: (type: string) => void
}

export function GroupHeaderRow({ row, cols, isFirst, onToggle }: GroupHeaderRowProps): JSX.Element {
  return (
    <tr className={cn('bg-muted/50', !isFirst && 'border-t border-border')}>
      <td className="p-0" colSpan={cols}>
        <button
          className="w-full flex items-center el-gap-sm px-0 py-2.5 hover:bg-primary/5 transition-colors text-left"
          onClick={() => onToggle(row.accountType)}
        >
          <svg
            className={cn('w-4 h-4 text-foreground-secondary transition-transform flex-shrink-0', row.isExpanded && 'rotate-90')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
          <span className="text-base font-semibold text-foreground">
            {ACCOUNT_TYPE_LABELS[row.accountType]}
          </span>
          <span className="text-sm text-foreground-secondary">
            ({row.count} {row.count === 1 ? 'account' : 'accounts'})
          </span>
        </button>
      </td>
    </tr>
  )
}

// =============================================================================
// Loading skeleton
// =============================================================================

function SkeletonGroupRow(): JSX.Element {
  return (
    <tr className="bg-muted/50 border-t border-border">
      <td className="px-4 py-2.5" colSpan={8}>
        <div className="flex items-center el-gap-sm">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-4 w-16 rounded-full" />
        </div>
      </td>
    </tr>
  )
}

function SkeletonDataRow({ keyId }: { keyId: string }): JSX.Element {
  return (
    <tr key={keyId} className="border-b border-border">
      <td className="px-3 py-2 pl-4"><Skeleton className="h-5 w-14" /></td>
      <td className="px-3 py-2"><Skeleton className="h-5 w-36" /></td>
      <td className="px-3 py-2"><Skeleton className="h-5 w-16 ml-auto" /></td>
      <td className="px-3 py-2"><Skeleton className="h-5 w-20" /></td>
      <td className="px-3 py-2"><Skeleton className="h-5 w-16" /></td>
      <td className="px-3 py-2"><Skeleton className="h-5 w-10" /></td>
      <td className="px-3 py-2"><Skeleton className="h-5 w-14 rounded-full" /></td>
      <td className="px-3 py-2 pr-4"><Skeleton className="h-5 w-12" /></td>
    </tr>
  )
}

export function LoadingSkeleton(): JSX.Element {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
        <ColGroup />
        <HeaderRow withAlignment={false} />
        <tbody>
          {ACCOUNT_TYPES.map(type => (
            <Fragment key={type}>
              <SkeletonGroupRow />
              {[0, 1].map(i => <SkeletonDataRow key={`skel-row-${type}-${i}`} keyId={`skel-row-${type}-${i}`} />)}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}
